import React from 'react';
import { TeamMember, ActiveFile } from '../types';
import { Badge } from './ui/Badge';
import { Switch } from './ui/Switch';
import { Button } from './ui/Button';
import { ClockIcon, ShieldCheckIcon, FileTextIcon, HashIcon, UsersIcon, ArrowRightLeftIcon } from 'lucide-react';
interface MemberDetailProps {
  member: TeamMember | null;
  onToggleActive: (id: string, isActive: boolean) => void;
  onToggleRule: (memberId: string, ruleId: string, isActive: boolean) => void;
  onOpenReassign: (sourceId?: string) => void;
}
function getStatusBadge(status: ActiveFile['status']) {
  const config: Record<ActiveFile['status'], {
    variant: 'success' | 'blue' | 'warning' | 'purple' | 'default';
    label: string;
  }> = {
    'In Progress': {
      variant: 'blue',
      label: 'In Progress'
    },
    'Pending Docs': {
      variant: 'warning',
      label: 'Pending Docs'
    },
    'Under Review': {
      variant: 'purple',
      label: 'Under Review'
    },
    'Clear to Close': {
      variant: 'success',
      label: 'Clear to Close'
    },
    Funded: {
      variant: 'success',
      label: 'Funded'
    }
  };
  const c = config[status];
  return <Badge variant={c.variant} data-id="element-27">{c.label}</Badge>;
}
function getFileTypeBadgeVariant(fileType: string): 'blue' | 'purple' | 'success' | 'warning' | 'error' | 'default' | 'gray' {
  const map: Record<string, 'blue' | 'purple' | 'success' | 'warning' | 'error' | 'default' | 'gray'> = {
    Conventional: 'blue',
    FHA: 'purple',
    VA: 'success',
    Jumbo: 'warning',
    HELOC: 'default'
  };
  return map[fileType] || 'gray';
}
export function MemberDetail({
  member,
  onToggleActive,
  onToggleRule,
  onOpenReassign
}: MemberDetailProps) {
  if (!member) {
    return <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 h-[calc(100vh-113px)] text-center px-4" data-id="element-28">
        <div className="bg-white p-4 rounded-full shadow-sm mb-4 border border-gray-100" data-id="element-29">
          <UsersIcon className="h-8 w-8 text-gray-300" data-id="element-30" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-31">
          Select a team member
        </h3>
        <p className="text-sm text-gray-500 max-w-sm" data-id="element-32">
          Choose someone from the list to view their assignment rules and active
          files.
        </p>
      </div>;
  }
  const capacityPct = Math.round(member.workload.activeFiles / member.workload.capacity * 100);
  const isAtCapacity = capacityPct >= 100;
  const isNearCapacity = capacityPct >= 80;
  return <div className="flex-1 bg-slate-50 h-[calc(100vh-113px)] overflow-y-auto flex flex-col" data-id="element-33">
      {/* Profile Header (Sticky) */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm px-6 py-5 flex-shrink-0" data-id="element-34">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-35">
          <div className="flex items-center space-x-4" data-id="element-36">
            <div className="relative" data-id="element-37">
              <img className="h-14 w-14 rounded-full object-cover border border-gray-200 shadow-sm" src={member.avatarUrl} alt="" data-id="element-38" />
              <span className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white ${member.isActive ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-39" />
            </div>
            <div data-id="element-40">
              <h2 className="text-xl font-bold text-gray-900 leading-tight" data-id="element-41">
                {member.name}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-42">{member.title}</p>
            </div>
          </div>

          <div className="flex items-center space-x-6 bg-gray-50 px-4 py-2 rounded-lg border border-gray-100" data-id="element-43">
            <div className="flex items-center space-x-3" data-id="element-44">
              <Switch checked={member.isActive} onChange={checked => onToggleActive(member.id, checked)} srLabel={`Toggle Round Robin for ${member.name}`} data-id="element-45" />
              <span className={`text-sm font-semibold ${member.isActive ? 'text-green-700' : 'text-gray-500'}`} data-id="element-46">
                {member.isActive ? 'Active' : 'Paused'}
              </span>
            </div>
            <div className="h-8 w-px bg-gray-200" data-id="element-47" />
            <div className="flex items-center text-sm text-gray-500" data-id="element-48">
              <ClockIcon className="h-4 w-4 mr-1.5 text-gray-400" data-id="element-49" />
              Last assigned:{' '}
              <span className="font-medium text-gray-700 ml-1" data-id="element-50">
                {member.lastAssigned}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-6" data-id="element-51">
            <div className="flex flex-col space-y-1.5 w-40" data-id="element-52">
              <div className="flex justify-between text-xs" data-id="element-53">
                <span className="font-semibold text-gray-700" data-id="element-54">
                  {member.workload.activeFiles} files
                </span>
                <span className="text-gray-400" data-id="element-55">
                  / {member.workload.capacity} cap
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-56">
                <div className={`h-2 rounded-full transition-all ${isAtCapacity ? 'bg-red-500' : isNearCapacity ? 'bg-amber-400' : 'bg-green-500'}`} style={{
                width: `${Math.min(capacityPct, 100)}%`
              }} data-id="element-57" />
              </div>
            </div>
            <Button onClick={() => onOpenReassign(member.id)} className="shadow-sm" data-id="element-58">
              <ArrowRightLeftIcon className="h-4 w-4 mr-2" data-id="element-59" />
              Reassign
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8 flex-1" data-id="element-60">
        {/* Assignment Rules Section */}
        <section data-id="element-61">
          <div className="flex items-center mb-4" data-id="element-62">
            <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-63" />
            <h3 className="text-lg font-bold text-gray-900" data-id="element-64">
              Assignment Rules
            </h3>
            <span className="ml-3 bg-gray-200 text-gray-700 py-0.5 px-2.5 rounded-full text-xs font-semibold" data-id="element-65">
              {member.rules.length}
            </span>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-id="element-66">
            {member.rules.map(rule => <div key={rule.id} className={`flex items-center justify-between bg-white rounded-lg border px-5 py-4 shadow-sm transition-colors ${rule.isActive ? 'border-gray-200' : 'border-gray-200 bg-gray-50/50'}`} data-id="element-67">
                <div className="flex items-center space-x-4 min-w-0" data-id="element-68">
                  <Switch checked={rule.isActive} onChange={checked => onToggleRule(member.id, rule.id, checked)} srLabel={`Toggle ${rule.role} on ${rule.fileType}`} data-id="element-69" />
                  <div className="flex items-center space-x-2 min-w-0" data-id="element-70">
                    <Badge variant={getFileTypeBadgeVariant(rule.fileType)} data-id="element-71">
                      {rule.fileType}
                    </Badge>
                    <span className="text-gray-300" data-id="element-72">→</span>
                    <Badge variant="gray" data-id="element-73">{rule.role}</Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-6 text-sm text-gray-500 flex-shrink-0 ml-4" data-id="element-74">
                  <div className="flex items-center" title="Total assignments" data-id="element-75">
                    <HashIcon className="h-4 w-4 mr-1.5 text-gray-400" data-id="element-76" />
                    <span className="font-semibold text-gray-700" data-id="element-77">
                      {rule.assignedCount}
                    </span>
                    <span className="ml-1 hidden sm:inline text-xs" data-id="element-78">
                      assigned
                    </span>
                  </div>
                  <div className="flex items-center" title="Last assigned" data-id="element-79">
                    <ClockIcon className="h-4 w-4 mr-1.5 text-gray-400" data-id="element-80" />
                    {rule.lastAssigned}
                  </div>
                  {!rule.isActive && <span className="text-amber-600 font-bold text-[10px] uppercase tracking-wider bg-amber-50 px-2 py-1 rounded" data-id="element-81">
                      Paused
                    </span>}
                </div>
              </div>)}
          </div>
        </section>

        {/* Active Files Section */}
        <section data-id="element-82">
          <div className="flex items-center mb-4" data-id="element-83">
            <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-84" />
            <h3 className="text-lg font-bold text-gray-900" data-id="element-85">Active Files</h3>
            <span className="ml-3 bg-blue-100 text-blue-700 py-0.5 px-2.5 rounded-full text-xs font-semibold" data-id="element-86">
              {member.activeFiles.length}
            </span>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-87">
            {member.activeFiles.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-88">
                <FileTextIcon className="h-8 w-8 mx-auto text-gray-300 mb-3" data-id="element-89" />
                <p data-id="element-90">No active files currently assigned.</p>
              </div> : <div className="overflow-x-auto" data-id="element-91">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-92">
                  <thead className="bg-gray-50" data-id="element-93">
                    <tr data-id="element-94">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-95">
                        Loan #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-96">
                        Borrower
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-97">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-98">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-99">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-100">
                        Assigned
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-101">
                    {member.activeFiles.map(file => <tr key={file.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-102">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-103">
                          <span className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer" data-id="element-104">
                            {file.loanNumber}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-105">
                          <span className="text-sm text-gray-900 font-medium" data-id="element-106">
                            {file.borrowerName}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-107">
                          <Badge variant={getFileTypeBadgeVariant(file.fileType)} data-id="element-108">
                            {file.fileType}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-109">
                          <Badge variant="gray" data-id="element-110">{file.role}</Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-111">
                          {getStatusBadge(file.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-112">
                          <span className="text-sm text-gray-500" data-id="element-113">
                            {file.assignedDate}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </section>
      </div>
    </div>;
}